<template>
  <div class="producer-settings">
    <div class="form-item">
      <input
        type="range"
        :min="minSellingPrice"
        :max="maxSellingPrice"
        :step="sellingPriceStepSize"
        v-model="defaultSellingPriceUserValue"
        :disabled="!defaultSellingPricesLoaded"
      />

      <div v-if="defaultSellingPricesLoaded" class="default-selling-price">
        <div class="default-selling-price__label">{{ t('settings.currentNetPrice') }}</div>
        <output class="default-selling-price__value">
          {{
            defaultSellingPrice ? formatPowerPrice(defaultSellingPrice) : '-'
          }}
        </output>
      </div>

      <div v-if="showNextDefaultSellingPriceAndDate" class="default-selling-price">
        <div class="default-selling-price__label">
          {{ nextDefaultSellingPriceLabel }} {{ t('settings.netSuffix') }}
        </div>
        <output class="default-selling-price__value">
          {{ formatPowerPrice(nextDefaultSellingPrice) }}
        </output>
      </div>
    </div>

    <div :class="actionsClasses">
      <a
        class="settings-actions__cancel"
        @click="initializeDefaultSellingPriceUserValue()"
      >
        {{ t('global.cancel') }}
      </a>

      <LinkButton
        type="small"
        icon-right="Arrow"
        form="defaultSellingPrice"
        :text="t('global.save')"
        :extra-classes="['button--primary', 'settings-actions__save']"
        @click.native="saveDefaultSellingPrice"
      />
    </div>
    <p><UnicodeIcon :bold="true" size="" color="black" icon="ⓘ" /> {{ t('settings.producerInfo1') }}</p>
    <p>{{ t('settings.producerInfo2') }}</p>
    <p>{{ t('settings.producerInfo3') }}</p>
    <p>{{ t('settings.producerInfo4') }}</p>
    <p>{{ t('settings.producerInfo5') }}</p>
  </div>
</template>

<script setup>
  import LinkButton from '@/components/LinkButton.vue'
  import { useSettingsStore } from '@/stores/useSettingsStore'
  import { useStrings } from '@/composables/useStrings.ts'
  import { useDate } from '@/composables/useDate.ts'
  import { useUnits } from '@/composables/useUnits.ts'
  import { computed, ref, watch } from 'vue'
  import UnicodeIcon from '@/components/icons/UnicodeIcon.vue'

  const settingsStore = useSettingsStore()
  const { t } = useStrings()
  const { formatPriceDate } = useDate()
  const { formatPowerPrice } = useUnits()

  const storedDefaultSellingPrice = computed(() => settingsStore.defaultSellingPrice)
  const storedNextDefaultSellingPrice = computed(() => settingsStore.nextDefaultSellingPrice)
  const defaultSellingPricesLoaded = computed(() => settingsStore.defaultSellingPricesLoaded)
  watch(defaultSellingPricesLoaded, (loaded) => {
    if (loaded) {
      initializeDefaultSellingPriceUserValue()
    }
  })

  const initializeDefaultSellingPriceUserValue = () => {
    // Use the next default selling price if it is trueish or 0.
    if (storedNextDefaultSellingPrice.value || storedNextDefaultSellingPrice.value === 0) {
      defaultSellingPriceUserValue.value = storedNextDefaultSellingPrice.value
    }
    else {
      defaultSellingPriceUserValue.value = storedDefaultSellingPrice.value
    }
  }

  let defaultSellingPriceUserValue = ref(null)
  initializeDefaultSellingPriceUserValue()

  const priceChangedByUser = computed(() => {
    if (!defaultSellingPricesLoaded.value) {
      return false
    }
    if (storedNextDefaultSellingPrice.value) {
      return defaultSellingPriceUserValue.value !== storedNextDefaultSellingPrice.value
    }
    else {
      return defaultSellingPriceUserValue.value !== storedDefaultSellingPrice.value
    }
  })

  const nextDefaultSellingPrice = computed(() => {
    if (priceChangedByUser.value) {
      return defaultSellingPriceUserValue.value
    }
    else if (storedNextDefaultSellingPrice.value) {
      return storedNextDefaultSellingPrice.value
    }
    else {
      return storedDefaultSellingPrice.value
    }
  })

  const defaultSellingPriceChanged = computed(() => {
    if (!defaultSellingPricesLoaded.value) {
      return false
    }
    if (storedNextDefaultSellingPrice.value) {
      return defaultSellingPriceUserValue.value !== storedNextDefaultSellingPrice.value
    }
    else {
      return defaultSellingPriceUserValue.value !== storedDefaultSellingPrice.value
    }
  })

  const showNextDefaultSellingPriceAndDate = computed(() => {
    return defaultSellingPriceChanged.value || storedNextDefaultSellingPrice.value
  })

  const minSellingPrice = computed(() => settingsStore.minDefaultSellingPrice)
  const maxSellingPrice = computed(() => settingsStore.maxDefaultSellingPrice)
  const sellingPriceStepSize = computed(() => settingsStore.sellingPriceStepSize)
  const defaultSellingPrice = computed(() => settingsStore.defaultSellingPrice)
  const nextDefaultSellingPriceLabel= computed(() => {
    let date = settingsStore.nextDefaultSellingPriceDate
    if (defaultSellingPriceChanged.value) {
      return t('efriends.nextPriceLabel')
    }
    return `Ab ${date ? formatPriceDate(date) : false}`
  })

  const actionsClasses = computed(() => {
    let classes = ['settings-actions']
    if (defaultSellingPriceChanged.value) {
      classes.push('settings-actions--visible')
    }
    return classes.join(' ')
  })

  const saveDefaultSellingPrice = async () => {
    if (!defaultSellingPriceChanged.value) {
      return false
    }
    await settingsStore.saveDefaultSellingPrice(defaultSellingPriceUserValue.value)
    initializeDefaultSellingPriceUserValue()
  }
</script>

<style lang="scss">
.default-selling-price {
  display: inline-block;
  margin-right: $spacer20;

  &__value {
    font-size: $font-size-lg;
  }
}
// The styles for .settings-actions have been moved by PG to PageSettings.vue
// to be reused for other settings forms on the settings page.
</style>
